/**
 * SWR-free core types. Importable from both server (route handlers) and
 * client (components) contexts without dragging SWR into the server bundle.
 */

import { z } from "zod";

export class ApiError extends Error {
  constructor(
    public status: number,
    public error: string,
  ) {
    super(error);
    this.name = "ApiError";
  }
}

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

/**
 * An Operation is one HTTP verb against one endpoint with one set of zod
 * schemas. Contract files in `./contracts/` export Operations; both
 * `apiCall` (client) and `createRoute` (server) accept them.
 *
 * `TParams` are typed path params (e.g. `{ id }` for `/tasks/[id]`).
 * `TQuery`  are typed URL search params.
 * `TBody`   is the request body (omit on GET).
 * `TResult` is the response data (already unwrapped from the envelope).
 */
export interface Operation<
  TParams = void,
  TQuery = void,
  TBody = void,
  TResult = unknown,
> {
  method: HttpMethod;
  path: (params: TParams) => string;
  params?: z.ZodType<TParams>;
  query?: z.ZodType<TQuery>;
  body?: z.ZodType<TBody>;
  response: z.ZodType<TResult>;
  /**
   * Reads this mutation makes stale on success (RO-5-01,
   * docs/rollover/REACT-ROLLOVER-PRD.md §13 R2 — the `revalidatePath`
   * replacement). Optional and additive: every existing contract keeps
   * compiling untouched. See `InvalidationTarget` below for the two shapes
   * and why a bare exact-key match isn't enough. Consumed automatically by
   * `apiCall` (client.ts) — declare it here and you get the mechanism for
   * free; nobody has to remember a `mutate(apiKey(...))` at the call site.
   * Only acted on for a non-`GET` method (a `GET` operation shouldn't have
   * one).
   *
   * Deliberately typed as the param-free `InvalidationTarget[]`, NOT
   * `InvalidationTarget<TParams, TQuery, TBody, TResult>[]`: this field's
   * type must not depend on this interface's own type parameters, or a
   * concrete `Operation<A,B,C,D>` stops being assignable to the widened
   * `Operation<A,B,unknown,unknown>` that generic helpers throughout this
   * file (and `use-cursor-list.ts`, `client-stream.ts`'s `useStream`) rely
   * on to erase a contract's specific body/result shape. Scoped targets
   * go through the generic factory `scoped<P>()` (return type checked
   * against the TARGET op's `TParams`), which erases to this type.
   */
  invalidates?: InvalidationTarget[];
}

// The conditional pairs `[T] extends [void]` (no distribution) with an
// `unknown extends T` guard so contracts that declare type parameters
// via `satisfies Operation<void, void, …>` (the inferred public type
// loses the void specialization at object-literal access — TS resolves
// the slot to `unknown` not `void`) still narrow to the optional shape
// at call sites. Without the guard, every caller of such a contract has
// to pass `{ params: undefined, query: undefined }` boilerplate.
export type Args<TParams, TQuery, TBody> = ([TParams] extends [void]
  ? { params?: undefined }
  : unknown extends TParams
    ? { params?: TParams }
    : { params: TParams }) &
  ([TQuery] extends [void]
    ? { query?: undefined }
    : unknown extends TQuery
      ? { query?: TQuery }
      : {} extends TQuery
        ? { query?: TQuery }
      : { query: TQuery }) &
  ([TBody] extends [void]
    ? { body?: undefined }
    : unknown extends TBody
      ? { body?: TBody }
      : { body: TBody });

/**
 * Brand marker on the object form of `InvalidationTarget`. Present only on
 * values produced by `scoped()` so a hand-written `{ op, params }` literal
 * (whose `params` return is unchecked against the target's `TParams`) is a
 * type error. The brand is a compile-time force-through-factory; runtime
 * only needs `"op" in target` (see `invalidationPrefixes`).
 */
export const INVALIDATION_SCOPED = "__excellentInvalidationScoped" as const;

/**
 * One invalidation target declared via `Operation.invalidates`.
 *
 * `apiKey(op, args)` is per-args — it bakes in path params AND the query
 * string. That makes exact-key invalidation (`mutate(apiKey(op, args))`,
 * the idiom this replaces) fine for a single known key, but a mutation
 * rarely knows the exact args of every currently-mounted read of the data
 * it just touched: a list is filtered, paginated, or simply mounted
 * somewhere else in the tree the mutation can't see. Exact-key
 * invalidation silently misses all of those — precisely the "stale UI,
 * no crash, no gate" failure PRD §13 R2 calls out.
 *
 * So a target invalidates a *family*: every cached key whose URL PATH
 * matches, with the query string treated as a wildcard (a predicate/prefix
 * match, not an exact key — see `invalidationPrefixes`/`keyInPrefixFamily`).
 * Two shapes, chosen deliberately per target:
 *
 *  - A bare `Operation` reference. Its own `TParams` must be `void` (no
 *    path params) — invalidates that operation's entire cache regardless
 *    of query, i.e. every filter/pagination variant refetches. Use this
 *    for a collection read with no natural per-resource scope from the
 *    mutating call (e.g. a task write invalidating the whole `tasksList`,
 *    whatever filters happen to be mounted).
 *  - `scoped(op, params)`. `params` computes the TARGET's path params from
 *    the MUTATING call's own args/result, narrowing the family to one
 *    resource while still wildcarding its query (e.g. posting a room
 *    message invalidates only `rooms.messages` for that room's `slug` —
 *    every page/cursor of THAT room, not every room). Required whenever
 *    the target's own path takes params: there's no value to default them
 *    to, and guessing wrong silently under- or over-invalidates.
 *
 * Deliberately NOT generic over the declaring operation's own
 * `TParams/TQuery/TBody/TResult` (see the comment on `Operation.invalidates`
 * above for why — it would make this field's type break `Operation<A,B,C,D>`
 * → `Operation<A,B,unknown,unknown>` assignability everywhere). The
 * generic factory `scoped<P>()` captures the TARGET's `TParams` and checks
 * the mapper's return against `P`, then erases to this param-free type:
 *
 * ```ts
 * invalidates: [
 *   scoped(rooms.messages, (args: Args<RoomsMessagesParams, void, RoomsPostBody>) => ({
 *     slug: args.params.slug,
 *   })),
 * ]
 * ```
 *
 * A typo like `{ slgu: args.params.slug }` is a compile error. Do NOT write
 * `{ op, params }` by hand — it is not assignable without the brand, and
 * that is intentional (RO-5-01 rework / #44311).
 */
export type InvalidationTarget =
  | Operation<void, any, any, any>
  | {
      readonly [INVALIDATION_SCOPED]: true;
      op: Operation<any, any, any, any>;
      params(args: any, result: any): unknown;
    };

/**
 * Type-safe scoped invalidation target. `params` must return the TARGET
 * op's `TParams` — a misspelled field (e.g. `slgu` instead of `slug`) is a
 * compile error, which is exactly the silent under-invalidation failure
 * PRD §13 R2 names. Returns the erased `InvalidationTarget` so
 * `Operation.invalidates` stays param-free and variance-safe.
 */
export function scoped<P>(
  op: Operation<P, any, any, any>,
  params: (args: any, result: any) => P,
): InvalidationTarget {
  return {
    [INVALIDATION_SCOPED]: true,
    op,
    params,
  };
}

/**
 * Resolve `Operation.invalidates` into the URL-path prefixes a successful
 * mutation should invalidate. Pure — no SWR import, so it's testable
 * without a DOM/React harness. `client.ts` turns each prefix into an actual
 * SWR `mutate` call via `keyInPrefixFamily`.
 */
export function invalidationPrefixes(
  invalidates: InvalidationTarget[] | undefined,
  args: unknown,
  result: unknown,
): string[] {
  if (!invalidates?.length) return [];
  return invalidates.map((target) =>
    "op" in target
      ? target.op.path(target.params(args, result))
      : target.path(undefined as never),
  );
}

/**
 * True when cache key `key` falls inside the family rooted at `prefix` —
 * the exact path, or that path followed by `?query`. Deliberately not a
 * substring match: `/api/rooms/general` must not swallow the unrelated
 * `/api/rooms/general2`.
 */
export function keyInPrefixFamily(key: unknown, prefix: string): boolean {
  return typeof key === "string" && (key === prefix || key.startsWith(`${prefix}?`));
}

export type Envelope<T> = { ok: true; data: T } | { ok: false; error: string };

export function envelopeSchema<T>(data: z.ZodType<T>): z.ZodType<Envelope<T>> {
  return z.union([
    z.object({ ok: z.literal(true), data }),
    z.object({ ok: z.literal(false), error: z.string() }),
  ]) as z.ZodType<Envelope<T>>;
}

export function buildUrl<TParams, TQuery>(
  op: Operation<TParams, TQuery, unknown, unknown>,
  params: TParams,
  query: TQuery,
): string {
  const path = op.path(params);
  if (query == null) return path;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
    if (v == null) continue;
    // Verification's product runtime is always real. Explicit demo remains a
    // legacy fixture-only input, but `env=real` is never transport state.
    if (k === "env" && v === "real") continue;
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Build the cache key for an operation — same string SWR uses as its dedupe
 * key, and the URL `apiCall` would fetch. Lives in types.ts (not client.ts)
 * because product `src/index.tsx` files evaluate it during server-side
 * registry discovery: importing it from a `"use client"` module makes Next's
 * RSC bundler treat it as a client reference and reject the call.
 */
export function apiKey<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody>,
): string {
  return buildUrl(op, args.params as TParams, args.query as TQuery);
}
